import React, { Dispatch, useEffect, useRef } from 'react'
import "ol/ol.css";
import { Feature, Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat, toLonLat } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { Draw } from 'ol/interaction';
import { Geometry, Polygon } from "ol/geom";
import VectorLayer from 'ol/layer/Vector';

export type Coordinate = {
	lat: number;
	lon: number
}

type MapCompPropsType = {
    polygon?: Dispatch<React.SetStateAction<Coordinate[]>>;
	polygonMulti?: Dispatch<React.SetStateAction<Coordinate[][]>>;
	defaultCenter?: [lat: number, lon: number]
	zoomLevel?: number;
	initialMultiPolygon?: Coordinate[][];
	clearButton: boolean
}

const MapComp = (props: MapCompPropsType) => {

	const BASE_URL = process.env.REACT_APP_BASE_URL
	const REACT_APP_COUNTRY = process.env.REACT_APP_COUNTRY

    const mapRef = useRef<HTMLDivElement | null>(null); // Reference for map div
	const olMapRef = useRef<Map | null>(null); // Store OpenLayers map instance
	const vectorSourceRef = useRef<VectorSource<Feature<Geometry>> | null>(null); 
    
    useEffect(() => {
		if (mapRef.current && !olMapRef.current) {
			// Vector layer for drawing
			vectorSourceRef.current = new VectorSource<Feature<Geometry>>({
				wrapX: false
			})
			const vectorLayer = new VectorLayer({
				source: vectorSourceRef.current,
			})
		
			olMapRef.current = new Map({
				target: mapRef.current,
				layers: [
					new TileLayer({
						source: new OSM({
							wrapX: false
						}),
					}),
					vectorLayer, // add vector layer for polygons
				],
				view: new View({
				center:
					REACT_APP_COUNTRY === "MALDIVES" ? fromLonLat([props.defaultCenter ? props.defaultCenter[0] : 73.2207, props.defaultCenter ? props.defaultCenter[1] : 3.2028]) : REACT_APP_COUNTRY === "SRI_LANKA" ? fromLonLat([80.7718, 7.8731]) : [0, 0],
					zoom: props.zoomLevel ? props.zoomLevel : 6,
				}),
			});
		
			// Add draw interaction for polygons
			const draw = new Draw({
				source: vectorSourceRef.current,
				type: "Polygon",
			});
			olMapRef.current.addInteraction(draw);
		
			// Event when polygon drawing is finished
			draw.on("drawend", (event) => {
				const polygon = event.feature.getGeometry() as Polygon;
				if (polygon) 
				{
					const coordinates = polygon.getCoordinates();
					//console.log("Polygon raw coordinates (EPSG:3857):", coordinates);
			
					// Convert to lon/lat
					const lonLatCoords = coordinates[0].map((coord) => toLonLat(coord));

					if (props.polygon)
					{
						const polygon = props.polygon;
						lonLatCoords.map(item => (
							polygon(prevState => [...prevState, {lat: item[0], lon: item[1]}])
						))
					}
					else if (props.polygonMulti)
					{	
						const polygon = props.polygonMulti
						const polygonMulti: Coordinate[] = []
						lonLatCoords.map(item => (
							polygonMulti.push({lat: item[1], lon: item[0]})
						))
						polygon(prevState => [...prevState, polygonMulti])
					}
					else
					{
						return
					}
					
					//console.log("Polygon coordinates in lon/lat:", lonLatCoords);
				}
			})
		}
	
		return () => {
		  olMapRef.current?.setTarget(undefined);
		}
	}, [])

	useEffect(() => {
		if (!props.initialMultiPolygon || !vectorSourceRef.current || !vectorSourceRef.current || !props.polygonMulti) return
		
		// Clear old shapes
		vectorSourceRef.current.clear()
	
		//console.log("DATA -> ", props.initialMultiPolygon)
		props.initialMultiPolygon.forEach(polygonCoords => {
			// Convert [{lat, lon}, ...] → [[lon, lat], ...]
			const transformed = polygonCoords.map(p => 
				fromLonLat([p.lon, p.lat])
			);
	
			// Create OpenLayers polygon
			const polygon = new Polygon([transformed]);
	
			const feature = new Feature({
				geometry: polygon
			})
	
			vectorSourceRef.current!.addFeature(feature)
		})
		props.polygonMulti(props.initialMultiPolygon)
	}, [props.initialMultiPolygon])

	const handleClearPolygon = () => {
		if (!vectorSourceRef.current || !props.polygonMulti) return
		// Clear old shapes
		vectorSourceRef.current.clear()
		props.polygonMulti([])
	}

    return (
        <div ref={mapRef} style={{ width: "100%", height: "500px", position: "relative" }}>
			{props.clearButton && (
				<button style={{
					position: 'absolute',
					top: '0.5rem',
					right: '0.5rem',
					padding: '0.4rem 2rem',
					borderRadius: '0.4rem',
					zIndex: '10',
					border: 'none',
					backgroundColor: 'var(--color-main-red)',
					color: 'var(--color-main-white)',
					cursor: 'pointer'
				}} onClick={(e) => handleClearPolygon()}>Clear</button>
			)}
		</div>
    )
}

export default MapComp